import { defineStore } from "pinia";
import {
  Contact,
  Extraction,
  Field,
  FieldType,
  JackBurtonApi,
  Search,
  SearchResult,
  SearchResultDisplayType,
  Suggestion,
  Value,
} from "@/services/api/sdk";
import { ApiConfiguration } from "@/services/api/api-configuration.ts";
import { computed, ref } from "vue";
import {
  isNonEmptySuggestion,
  groupSuggestionsByFieldId,
} from "@/utils/contacts/suggestions";
import { useCustomerEntryStore } from "@/store/contact/customer-entry";
import { useLiveCallStore } from "@/store/contact/live-call";

export enum ISuggestionSearchStatus {
  IDLE = "IDLE",
  SEARCHING = "SEARCHING",
  COMPLETE = "COMPLETE",
}

export enum ISuggestionStatus {
  CAPTURED = "CAPTURED",
  LISTENING = "LISTENING",
  SUGGESTED = "SUGGESTED",
}
export type ISuggestionBuckets = Record<ISuggestionStatus, Suggestion[]>;

export const useCallSuggestionsStore = defineStore("call-suggestions", () => {

  const customerEntryStore = useCustomerEntryStore();

  const extractions = ref<Extraction[]>([]);
  const suggestions = ref<Suggestion[]>([]);
  const searches = ref<Record<string, Search[]>>({});
  const searchResults = ref<Record<string, SearchResult>>({});
  const searchSuggestionsStatus = ref<Record<string, ISuggestionSearchStatus>>(
    {},
  );

  const availableSuggestions = computed(() => {
    const savedSuggestionIds = customerEntryStore.entries.filter(x => !!x.suggestionId).map(x => x.suggestionId);

    return suggestions.value.filter((x) => !savedSuggestionIds.includes(x.id));
  });

  const activePropertySearch = ref<Field | null>(null);
  const showPropertySearchPanel = computed<boolean>(
    () => !!activePropertySearch.value,
  );

  function $reset() {
    suggestions.value = [];
    searches.value = {};
    searchResults.value = {};
    searchSuggestionsStatus.value = {};
  }

  async function fetchSuggestionsForCall(call: Contact) {
    const api = new JackBurtonApi(ApiConfiguration);
    suggestions.value = await api.getContactSuggestions({
      contactId: call.id!,
    });
  }

  async function createSuggestion({
    contactId,
    fieldSlug,
    value,
  }: {
    contactId: number;
    fieldSlug: string;
    value: Value;
  }) {
    const api = new JackBurtonApi(ApiConfiguration);
    return await api.createSuggestion({
      contactId,
      createSuggestionRequest: {
        fieldSlug,
        value,
      },
    });
  }

  function addSuggestion(suggestion: Suggestion) {
    // Replace suggestions we already have instead of adding them twice
    const idx = suggestions.value.findIndex((x) => x.id === suggestion.id);
    if (idx !== -1) {
      suggestions.value.splice(idx, 1, suggestion);
      return;
    }
    suggestions.value.push(suggestion);
    for (const source of suggestion.sources || []) {
      if (!source.extraction) {
        continue;
      }
      extractions.value.push(source.extraction);
    }
  }

  function addSearch(search: Search) {
    const fieldId = search.fieldId!;
    if (!searches.value[fieldId]) {
      searches.value[fieldId] = [];
    }
    searches.value[fieldId].push(search);

    if (search.extraction) {
      extractions.value.push(search.extraction);
    }

    setSearchStatus(search.id!.toString(), ISuggestionSearchStatus.SEARCHING);
  }

  function setSearchStatus(
    searchId: string,
    searchStatus: ISuggestionSearchStatus,
  ) {
    searchSuggestionsStatus.value[searchId] = searchStatus;
  }

  function completeSearchRequest(
    searchRequest: Search,
    result: SearchResult,
  ): void {
    setSearchStatus(
      searchRequest.id!.toString(),
      ISuggestionSearchStatus.COMPLETE,
    );

    searchResults.value[searchRequest.id!] = result;
  }

  function cleanupSearchForSearchId(search: Search) {
    searches.value[search.fieldId!] = searches.value[search.fieldId!].filter(
      (_search: Search): boolean => _search.id !== search.id,
    );
  }

  function cleanupSearchResultsForSearchId(search: Search) {
    delete searchResults.value[search.id!];
  }

  const suggestionsByFieldId = computed<Record<number, Suggestion[]>>(() => {
    return groupSuggestionsByFieldId(availableSuggestions.value);
  });

  const extractionsByUtteranceId = computed<Record<number, Extraction[]>>(() => {
    return extractions.value.reduce((acc, extraction) => {
      if (!extraction.utteranceId) {
        return acc;
      }
      if (!acc[extraction.utteranceId]) {
        acc[extraction.utteranceId] = [];
      }
      acc[extraction.utteranceId].push(extraction);
      return acc;
    }, {} as Record<number, Extraction[]>);
  });

  const suggestionsByUtteranceId = computed<Record<number, Suggestion[]>>(() => {
    // Reduce the suggestions to get an array of suggestion per utterance
    return suggestions.value.filter(isNonEmptySuggestion).reduce((acc, suggestion) => {
      // Scan all the sources of a suggestion and map it for each utterance of the extraction
      for (const source of suggestion.sources ?? []) {
        // Bail out if we don't have any extraction or the extraction has no utterance
        if (!source.extraction || !source.extraction.utteranceId) {
          continue;
        }
        // Get or Create an array of suggestion for the given utteranceId
        const suggestions = acc[source.extraction.utteranceId] ?? [];
        suggestions.push(suggestion);
        acc[source.extraction.utteranceId!] = suggestions;
      }
      return acc;
    }, {} as Record<number, Suggestion[]>);
  });

  const searchesByUtteranceId = computed<Record<number, Search[]>>(() => {
    return Object.values(searches.value).reduce((acc, s) => {
      for (const search of s) {
        if (!acc[search.extraction?.utteranceId!]) {
          acc[search.extraction?.utteranceId!] = [];
        }
        acc[search.extraction?.utteranceId!].push(search);
      }
      return acc;
    },
      {} as Record<number, Search[]>,
    );
  });

  const suggestionsByStatus = computed<ISuggestionBuckets>(() => {
    const liveCallStore = useLiveCallStore();
    const customerEntryStore = useCustomerEntryStore();

    return liveCallStore.suggestionFields.reduce(
      (fields: ISuggestionBuckets, field: Field) => {
        const fieldHasNoSuggestions =
          !suggestionsByFieldId.value[field.id!]?.length &&
          !customerEntryStore.entriesByFieldId[field.id!];
        const fieldHasBeenCaptured =
          !!customerEntryStore.entriesByFieldId[field.id!];
        const fieldHasSuggestedValues = !!(
          !customerEntryStore.entriesByFieldId[field.id!] &&
          (!!suggestionsByFieldId.value[field.id!] || field.options?.length)
        );
        const fieldHasSearchResults = (searches.value[field.id!] || [])?.some(
          (search) => {
            return !!(
              searchResults.value[search.id!] &&
              searchResults.value[search.id!]?.items?.length
            );
          },
        );
        const isOpenEndedSearchField =
          field.type === FieldType.Search &&
          field.searchResultDisplayType !== SearchResultDisplayType.Inline;
        const isOptionFieldType =
          field.type === FieldType.Option && !!field.options?.length;

        switch (true) {
          case fieldHasBeenCaptured && !isOpenEndedSearchField:
            return {
              ...fields,
              [ISuggestionStatus.CAPTURED]: [
                ...fields[ISuggestionStatus.CAPTURED],
                field,
              ],
            };

          case isOptionFieldType:
          case fieldHasSearchResults:
          case fieldHasSuggestedValues:
            return {
              ...fields,
              [ISuggestionStatus.SUGGESTED]: [
                ...fields[ISuggestionStatus.SUGGESTED],
                field,
              ],
            };

          case isOpenEndedSearchField:
          case fieldHasNoSuggestions:
          default:
            return {
              ...fields,
              [ISuggestionStatus.LISTENING]: [
                ...fields[ISuggestionStatus.LISTENING],
                field,
              ],
            };
        }
      },
      {
        [ISuggestionStatus.SUGGESTED]: [],
        [ISuggestionStatus.CAPTURED]: [],
        [ISuggestionStatus.LISTENING]: [],
      },
    );
  });

  return {
    $reset,
    suggestions,
    searches,
    searchResults,
    searchSuggestionsStatus,
    fetchSuggestionsForCall,
    createSuggestion,
    addSuggestion,
    addSearch,
    setSearchStatus,
    completeSearchRequest,
    cleanupSearchForSearchId,
    cleanupSearchResultsForSearchId,
    suggestionsByFieldId,
    extractionsByUtteranceId,
    suggestionsByUtteranceId,
    searchesByUtteranceId,
    suggestionsByStatus,

    activePropertySearch,
    showPropertySearchPanel,
  };
});
